import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAllOrders, updateOrder, getOrder, deleteOrder } from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useToast } from '../../contexts/ToastContext';

function Orders() {
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkActionStatus, setBulkActionStatus] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const ordersPollingIntervalRef = useRef(null);

  // Get current user from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setCurrentUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  const isSuperAdmin = currentUser?.role?.name === 'super_admin';

  // Initial fetch with loading state
  const fetchOrders = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const token = localStorage.getItem('auth_token');
      if (!token) {
        if (showLoading) {
          showError('Authentication required. Please log in again.');
        }
        return;
      }
      const data = await getAllOrders(token, statusFilter);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      // Only show error on initial load, not on silent refreshes
      if (showLoading) {
        showError(error.message || 'Failed to load orders. Please refresh the page.');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [statusFilter, showError]);

  // Silent background refresh (no loading state)
  const refreshOrdersSilently = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const data = await getAllOrders(token, statusFilter);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      // Silently fail on background refreshes - errors are logged but not shown
      console.error('Failed to refresh orders silently:', error);
    }
  }, [statusFilter]);

  // Initial fetch and set up polling
  useEffect(() => {
    // Initial fetch with loading state
    fetchOrders(true);
    
    // Set up polling for orders list (silent background refresh every 3 seconds)
    ordersPollingIntervalRef.current = setInterval(() => {
      refreshOrdersSilently();
    }, 3000); // Poll every 3 seconds

    return () => {
      if (ordersPollingIntervalRef.current) {
        clearInterval(ordersPollingIntervalRef.current);
        ordersPollingIntervalRef.current = null;
      }
    };
  }, [fetchOrders, refreshOrdersSilently]);

  const handleViewOrder = async (order) => {
    setSelectedOrder(order);
    setIsOrderDetailOpen(true);
    setIsLoadingOrderDetails(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const fullOrder = await getOrder(order.id, token, true);
      setSelectedOrder(fullOrder);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      showError('Failed to load order details.');
    } finally {
      setIsLoadingOrderDetails(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      const token = localStorage.getItem('auth_token');
      // No delivery proof needed - courier handles delivery proof
      await updateOrder(orderId, newStatus, token, true, null);
      showSuccess(`Order status updated to ${newStatus}`);
      // Use silent refresh after status update (polling will continue after)
      await refreshOrdersSilently();
      // Update selected order if it's open
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      // Remove from selected orders if it was selected
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
    } catch (error) {
      console.error('Failed to update order status:', error);
      showError(error.message || 'Failed to update order status.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleToggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(order => order.id));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkActionStatus || selectedOrders.length === 0) {
      showError('Please select orders and choose a status.');
      return;
    }

    setIsBulkUpdating(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setIsBulkUpdating(false);
        return;
      }

      // Update each order individually
      const updatePromises = selectedOrders.map(orderId => 
        updateOrder(orderId, bulkActionStatus, token, true, null)
      );

      await Promise.all(updatePromises);
      showSuccess(`Successfully updated ${selectedOrders.length} order(s) to ${bulkActionStatus}`);
      
      // Clear selections and reset bulk action
      setSelectedOrders([]);
      setBulkActionStatus('');
      
      // Refresh orders
      await refreshOrdersSilently();
    } catch (error) {
      console.error('Failed to bulk update orders:', error);
      showError(error.message || 'Failed to update orders. Some orders may have been updated.');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!orderToDelete) return;

    setDeletingOrderId(orderToDelete.id);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setIsDeleteModalOpen(false);
        setOrderToDelete(null);
        return;
      }

      await deleteOrder(orderToDelete.id, token);
      showSuccess('Order deleted successfully');
      
      // Close modal and reset state
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
      
      // Refresh orders
      await fetchOrders(false);
    } catch (error) {
      console.error('Failed to delete order:', error);
      showError(error.message || 'Failed to delete order. Please try again.');
    } finally {
      setDeletingOrderId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setOrderToDelete(null);
    setDeletingOrderId(null);
  };


  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      processing: 'bg-blue-100 text-blue-800 border-blue-300',
      shipped: 'bg-purple-100 text-purple-800 border-purple-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
      pending_refund: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      refund_approved: 'bg-blue-100 text-blue-800 border-blue-300',
      refund_rejected: 'bg-red-100 text-red-800 border-red-300',
      pending_payment: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Filter orders by date range and search query
  const filterOrders = (ordersList) => {
    let filtered = ordersList;

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        orderDate.setHours(0, 0, 0, 0);
        
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return orderDate >= start && orderDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return orderDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return orderDate <= end;
        }
        return true;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(order => {
        // Search by order number
        const orderNumber = (order.order_number || '').toLowerCase();
        // Search by customer name
        const customerName = (order.user?.name || '').toLowerCase();
        // Search by customer email
        const customerEmail = (order.user?.email || '').toLowerCase();
        // Search by tracking number
        const trackingNumber = (order.tracking_number || '').toLowerCase();
        // Search by payment method
        const paymentMethod = (order.payment_method || '').toLowerCase();
        
        return orderNumber.includes(query) ||
               customerName.includes(query) ||
               customerEmail.includes(query) ||
               trackingNumber.includes(query) ||
               paymentMethod.includes(query);
      });
    }

    return filtered;
  };

  // Get filtered orders
  const filteredOrders = filterOrders(orders);

  const totalOrders = filteredOrders.length;
  const pendingOrders = filteredOrders.filter(o => o.status === 'pending').length;
  const deliveredOrders = filteredOrders.filter(o => o.status === 'delivered').length;
  const totalRevenue = filteredOrders
    .filter(o => o.status !== 'cancelled')
    .reduce((sum, order) => sum + parseFloat(order.total || 0), 0);

  const handleClearDateRange = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6 md:mb-8">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-1.5 h-8 md:h-10 bg-gradient-to-b from-primary via-primary-dark to-primary-dark rounded-full"></div>
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-1 md:mb-2">Orders</h1>
            <p className="text-xs md:text-sm text-slate-600 font-medium">View and manage customer orders</p>
          </div>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex flex-col items-start md:items-end gap-2">
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <button
              onClick={handleTodayFilter}
              className={`px-2.5 md:px-3 lg:px-4 py-1.5 md:py-2 rounded-lg font-semibold text-xs md:text-sm transition-all duration-200 whitespace-nowrap ${
                startDate && endDate && startDate === endDate && startDate === new Date().toISOString().split('T')[0]
                  ? 'bg-gradient-to-r from-primary via-primary-dark to-secondary text-white shadow-lg'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-2 border-blue-300'
              }`}
            >
              Today
            </button>
            <div className="flex items-center gap-1.5 md:gap-2">
              <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 md:px-3 py-1.5 md:py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs md:text-sm"
              />
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="px-2 md:px-3 py-1.5 md:py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs md:text-sm"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={handleClearDateRange}
                className="px-2 md:px-3 py-1.5 md:py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 border-2 border-blue-300 rounded-lg font-semibold text-xs transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-2.5 lg:gap-4 mb-4 md:mb-5 lg:mb-6">
        <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-blue-300/50 p-2 md:p-2.5 lg:p-4 shadow-lg md:shadow-xl shadow-blue-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-1 md:mb-1.5 lg:mb-2">
              <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Total Orders</span>
              <div className="w-6 h-6 md:w-7 md:h-7 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg">
                <svg className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <p className="text-base md:text-lg lg:text-2xl font-black text-blue-700">{totalOrders}</p>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-yellow-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-yellow-300/50 p-2 md:p-2.5 lg:p-4 shadow-lg md:shadow-xl shadow-yellow-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-yellow-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 opacity-70"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-1 md:mb-1.5 lg:mb-2">
              <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Pending</span>
              <div className="w-6 h-6 md:w-7 md:h-7 lg:w-10 lg:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg">
                <svg className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-base md:text-lg lg:text-2xl font-black text-yellow-700">{pendingOrders}</p>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-green-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-green-300/50 p-2 md:p-2.5 lg:p-4 shadow-lg md:shadow-xl shadow-green-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-green-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-green-600 opacity-70"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-1 md:mb-1.5 lg:mb-2">
              <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Delivered</span>
              <div className="w-6 h-6 md:w-7 md:h-7 lg:w-10 lg:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg">
                <svg className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-base md:text-lg lg:text-2xl font-black text-green-700">{deliveredOrders}</p>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-purple-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-purple-300/50 p-2 md:p-2.5 lg:p-4 shadow-lg md:shadow-xl shadow-purple-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-purple-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600 opacity-70"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-1 md:mb-1.5 lg:mb-2">
              <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Total Revenue</span>
              <div className="w-6 h-6 md:w-7 md:h-7 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg">
                <svg className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-sm md:text-base lg:text-2xl font-black text-purple-700 break-words">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedOrders.length > 0 && (
        <div className="bg-gradient-to-r from-primary/10 via-primary-dark/10 to-secondary/10 rounded-xl md:rounded-2xl border-2 border-primary/30 shadow-lg md:shadow-xl p-3 md:p-4 mb-4 md:mb-5 lg:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-xs md:text-sm font-bold text-gray-900">
                {selectedOrders.length} order(s) selected
              </span>
              <button
                onClick={() => {
                  setSelectedOrders([]);
                  setBulkActionStatus('');
                }}
                className="text-xs md:text-sm text-gray-600 hover:text-gray-900 font-semibold underline"
              >
                Clear Selection
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3 flex-1">
              <select
                value={bulkActionStatus}
                onChange={(e) => setBulkActionStatus(e.target.value)}
                className="px-3 md:px-4 py-2 md:py-2.5 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs md:text-sm font-semibold bg-white"
              >
                <option value="">Select Status...</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={!bulkActionStatus || isBulkUpdating}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg font-semibold text-xs md:text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isBulkUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Update Selected</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-slate-200/80 shadow-lg md:shadow-xl p-2.5 md:p-3 lg:p-4 mb-4 md:mb-5 lg:mb-6">
        <div className="flex flex-col gap-3 md:gap-4">
          {/* Search Field */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <label className="text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">Search:</label>
            <div className="relative flex-1 w-full sm:max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by order number, customer name, email, tracking number..."
                className="w-full px-3 md:px-4 py-2 pl-9 md:pl-10 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs md:text-sm"
              />
              <svg
                className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 md:right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 md:gap-3">
            <span className="text-xs md:text-sm font-semibold text-gray-700 whitespace-nowrap">Filter by Status:</span>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {['all', 'pending', 'processing', 'shipped', 'delivered', 'pending_refund', 'refund_approved', 'refund_rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-semibold text-xs md:text-sm transition-all duration-200 whitespace-nowrap ${
                    statusFilter === status
                      ? 'bg-gradient-to-r from-primary via-primary-dark to-secondary text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl p-12 text-center">
          <p className="text-slate-600 font-semibold text-lg">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl p-12 text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl blur-xl opacity-50"></div>
            <div className="relative w-24 h-24 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl flex items-center justify-center border-2 border-slate-200/60 shadow-lg">
              <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
          </div>
          <p className="text-slate-600 font-semibold text-lg">No orders found</p>
          <p className="text-slate-400 text-sm mt-2">Orders will appear here once customers place them</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 420px)' }}>
          <div className="overflow-x-auto flex-1 overflow-y-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100 w-12">
                    <input
                      type="checkbox"
                      checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100">Order #</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100">Customer</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100 hidden md:table-cell">Date</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100">Total</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100">Status</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100 hidden lg:table-cell">Tracking #</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100 hidden lg:table-cell">Payment</th>
                  <th className="px-3 md:px-4 lg:px-6 py-3 md:py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-gray-50 to-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className={`hover:bg-gray-50 transition-colors ${selectedOrders.includes(order.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => handleToggleOrderSelection(order.id)}
                        className="w-4 h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                      />
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                      <div className="text-xs md:text-sm font-bold text-gray-900">{order.order_number}</div>
                      <div className="text-xs text-gray-500 md:hidden mt-1">{formatDate(order.created_at)}</div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4">
                      <div className="text-xs md:text-sm font-semibold text-gray-900">{order.user?.name || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{order.user?.email || ''}</div>
                      <div className="text-xs text-gray-500 lg:hidden mt-1">
                        {order.tracking_number && (
                          <span className="inline-flex items-center gap-1">
                            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {order.tracking_number}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 lg:hidden mt-1 capitalize">
                        {order.payment_method?.replace('_', ' ') || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-xs md:text-sm text-gray-900">{formatDate(order.created_at)}</div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                      <div className="text-xs md:text-sm font-bold text-gray-900">{formatCurrency(order.total)}</div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                      {(order?.payment_status === 'pending_payment' || order?.payment_status === 'failed') ? (
                        <div className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs font-bold border-2 ${getStatusColor('pending_payment')} w-full max-w-[140px] md:max-w-none text-center`}>
                          Pending Payment
                        </div>
                      ) : (
                        <select
                          value={order.status || 'pending'}
                          onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                          disabled={updatingStatus === order.id}
                          className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs font-bold border-2 ${getStatusColor(order.status)} cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-[140px] md:max-w-none`}
                        >
                          {order.status === 'pending_refund' || order.status === 'refund_approved' || order.status === 'refund_rejected' ? (
                            <>
                              <option value="pending_refund">Pending Refund</option>
                              <option value="refund_approved">Approve Refund</option>
                              <option value="refund_rejected">Reject Refund</option>
                            </>
                          ) : (
                            <>
                              {!order.tracking_number && <option value="pending">Pending</option>}
                              {!order.tracking_number && <option value="processing">Processing</option>}
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </>
                          )}
                        </select>
                      )}
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap hidden lg:table-cell">
                      {order.tracking_number ? (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs font-bold text-green-700 font-mono">{order.tracking_number}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-xs text-gray-600 capitalize">
                        {order.payment_method?.replace('_', ' ') || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-3 lg:px-4 py-1.5 md:py-2 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg font-semibold text-xs md:text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap"
                        >
                          <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="hidden sm:inline">View Details</span>
                          <span className="sm:hidden">View</span>
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDeleteOrder(order)}
                            disabled={deletingOrderId === order.id}
                            className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-xs md:text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Order (Super Admin Only)"
                          >
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {isOrderDetailOpen && selectedOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-modal-pop">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 md:px-6 py-4 md:py-5 flex items-center justify-between sticky top-0 z-10 shadow-lg shadow-primary/30">
              <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg md:text-2xl font-bold text-white truncate">Order Details</h2>
                  <p className="text-xs text-white/80 truncate">{selectedOrder.order_number}</p>
                </div>
                {isLoadingOrderDetails && (
                  <div className="flex items-center gap-2 ml-4">
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-xs text-white font-medium">Loading...</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setIsOrderDetailOpen(false);
                  setSelectedOrder(null);
                  setIsLoadingOrderDetails(false);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Customer Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-semibold">Name:</span> {selectedOrder.user?.name || 'N/A'}</div>
                    <div><span className="font-semibold">Email:</span> {selectedOrder.user?.email || 'N/A'}</div>
                    <div><span className="font-semibold">Phone:</span> {selectedOrder.phone || 'N/A'}</div>
                  </div>
                </div>

                {/* Order Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Order Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-semibold">Date:</span> {formatDate(selectedOrder.created_at)}</div>
                    <div><span className="font-semibold">Status:</span> 
                      {(selectedOrder?.payment_status === 'pending_payment' || selectedOrder?.payment_status === 'failed') ? (
                        <span className={`ml-2 px-3 py-1 rounded-lg text-xs font-bold border-2 ${getStatusColor('pending_payment')} inline-block`}>
                          Pending Payment
                        </span>
                      ) : (
                        <select
                          value={selectedOrder.status || 'pending'}
                          onChange={(e) => handleStatusUpdate(selectedOrder.id, e.target.value)}
                          disabled={updatingStatus === selectedOrder.id}
                          className={`ml-2 px-3 py-1 rounded-lg text-xs font-bold border-2 ${getStatusColor(selectedOrder.status)} cursor-pointer disabled:opacity-50`}
                        >
                          {selectedOrder.status === 'pending_refund' || selectedOrder.status === 'refund_approved' || selectedOrder.status === 'refund_rejected' ? (
                            <>
                              <option value="pending_refund">Pending Refund</option>
                              <option value="refund_approved">Approve Refund</option>
                              <option value="refund_rejected">Reject Refund</option>
                            </>
                          ) : (
                            <>
                              {!selectedOrder.tracking_number && <option value="pending">Pending</option>}
                              {!selectedOrder.tracking_number && <option value="processing">Processing</option>}
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </>
                          )}
                        </select>
                      )}
                    </div>
                    {selectedOrder.tracking_number && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Tracking #:</span>
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 rounded-lg border border-green-300">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-mono font-bold text-green-700 text-xs">{selectedOrder.tracking_number}</span>
                        </div>
                      </div>
                    )}
                    <div><span className="font-semibold">Payment:</span> {selectedOrder.payment_method?.replace('_', ' ') || 'N/A'}</div>
                    <div><span className="font-semibold">Total:</span> {formatCurrency(selectedOrder.total)}</div>
                    {selectedOrder.status === 'delivered' && selectedOrder.delivery_otp && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Delivery OTP</p>
                            <p className="text-2xl font-bold font-mono text-blue-700 tracking-widest">{selectedOrder.delivery_otp}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {selectedOrder.otp_expires_at 
                                ? `Expires: ${formatDate(selectedOrder.otp_expires_at)}`
                                : 'Valid for 24 hours'}
                            </p>
                          </div>
                          <div className="text-right">
                            {selectedOrder.customer_confirmation === 'confirmed' && (
                              <div className="flex items-center gap-1 text-green-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-xs font-bold">Verified</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">
                          Share this OTP with the customer to confirm delivery. Customer must enter this OTP to verify receipt.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>


              {/* Shipping Address */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-2">Shipping Address</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line">{selectedOrder.shipping_address || 'N/A'}</p>
              </div>

              {/* Order Items */}
              <div className="mb-4 md:mb-6">
                <h3 className="font-bold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Order Items</h3>
                {(() => {
                  // Handle both camelCase and snake_case from API
                  // Laravel returns snake_case by default
                  const orderItems = selectedOrder.order_items || selectedOrder.orderItems || [];
                  const itemsCount = Array.isArray(orderItems) ? orderItems.length : 0;
                  return itemsCount > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
                      {orderItems.map((item, index) => (
                        <div key={item.id || index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-200 mb-2">
                            <img 
                              src={item.product?.images?.[0] || item.product?.image || 'https://via.placeholder.com/100'} 
                              alt={item.product?.name || 'Product'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/100';
                              }}
                            />
                          </div>
                          <h4 className="font-semibold text-gray-900 text-xs mb-1 line-clamp-2">{item.product?.name || 'Product'}</h4>
                          <div className="text-xs text-gray-600">
                            <div>Qty: {item.quantity}</div>
                            <div className="font-semibold text-gray-900 mt-0.5">{formatCurrency(item.subtotal)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm">No items found</p>
                  );
                })()}
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
                  <h3 className="font-bold text-gray-900 mb-2">Order Notes</h3>
                  <p className="text-sm text-gray-700">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
        </div>
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && orderToDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 md:p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-modal-pop">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-4 md:px-6 py-4 md:py-5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-white">Delete Order</h2>
                  <p className="text-xs text-white/80">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 md:p-6">
              <div className="mb-4">
                <p className="text-sm md:text-base text-gray-700 mb-2">
                  Are you sure you want to delete this order?
                </p>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-600">Order Number:</span>
                      <span className="text-gray-900 font-mono">{orderToDelete.order_number}</span>
                    </div>
                    {orderToDelete.invoice_number && (
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-600">Invoice:</span>
                        <span className="text-gray-900 font-mono text-xs">{orderToDelete.invoice_number}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-600">Customer:</span>
                      <span className="text-gray-900">{orderToDelete.user?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-600">Total:</span>
                      <span className="text-gray-900 font-bold">{formatCurrency(orderToDelete.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 mb-4">
                <p className="text-xs md:text-sm text-red-800 font-semibold">
                  ⚠️ Warning: This will soft delete the order. The order will be removed from the list but can be recovered from the database if needed.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 md:px-6 py-4 bg-gray-50 rounded-b-2xl flex items-center justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={deletingOrderId === orderToDelete.id}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold text-sm md:text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingOrderId === orderToDelete.id}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-red-600 via-red-700 to-red-800 hover:from-red-700 hover:via-red-800 hover:to-red-900 text-white rounded-lg font-semibold text-sm md:text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deletingOrderId === orderToDelete.id ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Delete Order</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;
